// @ts-nocheck
"use client";

import { useState } from "react";
import { Shield, Lock, Unlock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { TradingMode } from "@/types/trade/strategy-signals";

interface TradingModeToggleProps {
  currentMode: TradingMode;
  isAuthenticated: boolean;
  demoQuantity: number;
  liveQuantity: number;
  onModeChange: (mode: TradingMode, passkey?: string) => void;
  onQuantityChange: (mode: TradingMode, quantity: number) => void;
}

export function TradingModeToggle({
  currentMode,
  isAuthenticated,
  demoQuantity,
  liveQuantity,
  onModeChange,
  onQuantityChange,
}: TradingModeToggleProps) {
  const [showPasskeyDialog, setShowPasskeyDialog] = useState(false);
  const [passkey, setPasskey] = useState("");
  const [error, setError] = useState("");
  const [showQuantityDialog, setShowQuantityDialog] = useState(false);
  const [quantityInput, setQuantityInput] = useState("");

  const handleModeSwitch = () => {
    if (currentMode === "DEMO") {
      // Switching to LIVE - require passkey
      setShowPasskeyDialog(true);
    } else {
      // Switching to DEMO - no auth required
      onModeChange("DEMO");
    }
  };

  const handlePasskeySubmit = () => {
    if (!passkey || passkey.length < 6) {
      setError("Passkey must be at least 6 characters");
      return;
    }

    // TODO: Validate passkey with backend
    // For now, accept any 6+ character passkey
    onModeChange("LIVE", passkey);
    setShowPasskeyDialog(false);
    setPasskey("");
    setError("");
  };

  const handleQuantityUpdate = () => {
    const qty = parseFloat(quantityInput);
    if (isNaN(qty) || qty <= 0) {
      setError("Please enter a valid quantity");
      return;
    }

    onQuantityChange(currentMode, qty);
    setShowQuantityDialog(false);
    setQuantityInput("");
    setError("");
  };

  return (
    <>
      <div className="flex items-center space-x-2">
        {/* Mode Indicator */}
        <div
          className={cn(
            "h-7 px-3 rounded-md text-xs font-semibold flex items-center space-x-1.5 border transition-all cursor-pointer",
            currentMode === "DEMO"
              ? "bg-blue-500/20 border-blue-500/50 text-blue-400 hover:bg-blue-500/30"
              : "bg-emerald-500/20 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/30"
          )}
          onClick={handleModeSwitch}
          title={currentMode === "DEMO" ? "Click to enable Live Trading" : "Click to switch to Demo Mode"}
        >
          {currentMode === "DEMO" ? (
            <>
              <Shield className="h-3.5 w-3.5" />
              <span>DEMO</span>
            </>
          ) : (
            <>
              <Lock className="h-3.5 w-3.5" />
              <span>LIVE</span>
              {isAuthenticated && <span className="text-[10px] opacity-75">✓</span>}
            </>
          )}
        </div>

        {/* Quantity Display */}
        <div
          className="h-7 px-2 rounded-md text-xs font-medium bg-zinc-800/50 border border-zinc-700/50 flex items-center cursor-pointer hover:bg-zinc-800/70"
          onClick={() => {
            setQuantityInput(currentMode === "DEMO" ? demoQuantity.toString() : liveQuantity.toString());
            setShowQuantityDialog(true);
          }}
          title="Click to change quantity"
        >
          <span className="text-zinc-400 mr-1">Qty:</span>
          <span className="text-white font-semibold">
            {currentMode === "DEMO" ? demoQuantity : liveQuantity}
          </span>
        </div>
      </div>

      {/* Passkey Authentication Dialog */}
      <Dialog open={showPasskeyDialog} onOpenChange={setShowPasskeyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Lock className="h-5 w-5 text-emerald-500" />
              <span>Unlock Live Trading</span>
            </DialogTitle>
            <DialogDescription>
              Enter your passkey to enable real trade execution. Your account will be charged for actual trades.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="passkey">Passkey</Label>
              <Input
                id="passkey"
                type="password"
                placeholder="Enter your passkey"
                value={passkey}
                onChange={(e) => {
                  setPasskey(e.target.value);
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handlePasskeySubmit();
                  }
                }}
                className="font-mono"
              />
              {error && (
                <p className="text-sm text-red-500 flex items-center space-x-1">
                  <AlertTriangle className="h-3 w-3" />
                  <span>{error}</span>
                </p>
              )}
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3">
              <p className="text-xs text-amber-400 flex items-start space-x-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  <strong>Warning:</strong> Live trading will execute real orders on the exchange using your funds.
                  Make sure you understand the risks involved.
                </span>
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPasskeyDialog(false);
                setPasskey("");
                setError("");
              }}
            >
              Cancel
            </Button>
            <Button onClick={handlePasskeySubmit} className="bg-emerald-600 hover:bg-emerald-700">
              <Unlock className="h-4 w-4 mr-2" />
              Unlock Live Trading
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quantity Change Dialog */}
      <Dialog open={showQuantityDialog} onOpenChange={setShowQuantityDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set {currentMode === "DEMO" ? "Demo" : "Live"} Quantity</DialogTitle>
            <DialogDescription>
              {currentMode === "DEMO"
                ? "Set the quantity for demo trades (no real money)"
                : "Set the quantity for live trades (uses real funds)"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                id="quantity"
                type="number"
                step="0.0001"
                min="0"
                placeholder="0.001"
                value={quantityInput}
                onChange={(e) => {
                  setQuantityInput(e.target.value);
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleQuantityUpdate();
                  }
                }}
              />
              {error && (
                <p className="text-sm text-red-500 flex items-center space-x-1">
                  <AlertTriangle className="h-3 w-3" />
                  <span>{error}</span>
                </p>
              )}
            </div>

            {currentMode === "LIVE" && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3">
                <p className="text-xs text-amber-400">
                  This quantity will be used for all live trades. Make sure you have sufficient balance.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowQuantityDialog(false);
                setQuantityInput("");
                setError("");
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleQuantityUpdate}>Update Quantity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

